import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Skeleton,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ROUTES } from '../../../types/global/routes.types';
import {
    fetchBrandOpsAutomations,
    fetchBrandOpsCalendar,
    fetchBrandOpsConversationMessages,
    fetchBrandOpsConversations,
    fetchBrandOpsInbox,
    fetchBrandOpsInboxStatus,
    runBrandOpsAiPlayground,
    sendBrandOpsInboxMessage,
    toggleBrandOpsAutomation,
} from '../../../redux/brandOps/actions';
import {
    brandOpsAiBusySelector,
    brandOpsAutomationsSelector,
    brandOpsCalendarSelector,
    brandOpsConversationMessagesSelector,
    brandOpsConversationsSelector,
    brandOpsFetchingAutomationsSelector,
    brandOpsFetchingConversationsSelector,
    brandOpsFetchingInboxSelector,
    brandOpsFetchingMessagesSelector,
    brandOpsInboxSelector,
    brandOpsInboxStatusSelector,
    brandOpsMutatingAutomationSelector,
    brandOpsLastNewDmSelector,
} from '../../../redux/brandOps/selectors';
import { showToastMessage, useDispatchPromise } from '../../../utils/AppUtils';
import { getAiSuggestedText, HUMAN_ONLY_MESSAGE } from '../../../redux/brandOps/brandOpsHelpers';
import type {
    IBrandOpsAiPlaygroundResult,
    IBrandOpsAutomationRule,
    IBrandOpsInboxMessage,
} from '../../../redux/brandOps/types';
import { BRAND_LUXE_MUTED, BRAND_LUXE_PRIMARY } from '../brandDashboardTokens';
import {
    BrandManageGate,
    ManageGoldButton,
    ManagePageHeader,
    ManageSurface,
    MANAGE_PAGE_SX,
    useBrandManageGate,
} from './manageShared';
import BrandManageAutomationRuleModal from './BrandManageAutomationRuleModal';

/** Derive a short human-readable label from peer name or numeric ID */
function formatPeerName(peerName: string | null | undefined, peerId: string | null | undefined): string {
    if (peerName) return peerName;
    if (!peerId) return 'Instagram User';
    const suffix = peerId.slice(-6);
    return `User ···${suffix}`;
}

/** Pick a consistent gradient from an ID string */
const AVATAR_GRADIENTS = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    'linear-gradient(135deg, #fda085 0%, #f6d365 100%)',
    'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)',
];
function avatarGradient(id: string | null | undefined): string {
    if (!id) return AVATAR_GRADIENTS[0];
    const n = id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return AVATAR_GRADIENTS[n % AVATAR_GRADIENTS.length];
}

/** Format a date string as a relative or absolute label */
function relativeTime(dateStr: string | null | undefined): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function BrandManageInboxPage() {
    const dispatch = useDispatch();
    const dispatchP = useDispatchPromise();
    const { brandId } = useBrandManageGate();
    const [tab, setTab] = useState<'dms' | 'activity'>('dms');
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [reply, setReply] = useState('');
    const [automationModalOpen, setAutomationModalOpen] = useState(false);
    const [editingRule, setEditingRule] = useState<IBrandOpsAutomationRule | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    // Per-conversation message cache: conversationId → messages[]
    // undefined = never fetched; [] = fetched but empty; [...] = has messages
    const [messageCache, setMessageCache] = useState<Record<string, IBrandOpsInboxMessage[]>>({});
    // Tracks which conversationId the most recent fetch was dispatched FOR.
    // Using a ref (not state) so it updates synchronously without causing extra renders.
    const fetchingForIdRef = useRef<string | null>(null);
    // Optimistic messages keyed by conversationId
    const [optimisticMsgs, setOptimisticMsgs] = useState<
        Record<string, Array<{ id: string; text: string; created_at: string; failed?: boolean }>>
    >({});

    const activity = useSelector(brandOpsInboxSelector);
    const fetchingActivity = useSelector(brandOpsFetchingInboxSelector);
    const conversations = useSelector(brandOpsConversationsSelector);
    const fetchingConversations = useSelector(brandOpsFetchingConversationsSelector);
    const messages = useSelector(brandOpsConversationMessagesSelector);
    const fetchingMessages = useSelector(brandOpsFetchingMessagesSelector);
    const aiBusy = useSelector(brandOpsAiBusySelector);
    const inboxStatus = useSelector(brandOpsInboxStatusSelector);
    const automations = useSelector(brandOpsAutomationsSelector);
    const fetchingAutomations = useSelector(brandOpsFetchingAutomationsSelector);
    const mutatingAutomation = useSelector(brandOpsMutatingAutomationSelector);
    const calendarItems = useSelector(brandOpsCalendarSelector);
    const lastNewDm = useSelector(brandOpsLastNewDmSelector);

    const igPublishedPosts = useMemo(
        () => calendarItems.filter(c => c.platform === 'instagram' && c.status === 'published'),
        [calendarItems],
    );

    useEffect(() => {
        if (!brandId) return;
        dispatch(fetchBrandOpsConversations({ brandId, force: true }));
        dispatch(fetchBrandOpsInbox({ brandId, force: true }));
        dispatch(fetchBrandOpsInboxStatus({ brandId }));
        dispatch(fetchBrandOpsAutomations({ brandId }));
        dispatch(fetchBrandOpsCalendar({ brandId }));
    }, [brandId, dispatch]);

    // Refresh conversations list when a new DM arrives via WebSocket
    useEffect(() => {
        if (!brandId || !lastNewDm) return;
        dispatch(fetchBrandOpsConversations({ brandId, force: true }));
        // If the new DM is not in the currently open conversation, show a toast
        if (selectedId == null) {
            showToastMessage('New Instagram DM received', 'info');
        }
    }, [lastNewDm]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!brandId || !selectedId) return;
        // Record WHICH conversation we're fetching for before dispatching.
        // This must happen before the dispatch so the ref is set when messages arrive.
        fetchingForIdRef.current = selectedId;
        dispatch(fetchBrandOpsConversationMessages({ brandId, conversationId: selectedId }));
    }, [brandId, selectedId, dispatch]);

    // Cache update: triggered only when Redux messages (or fetchingMessages) change.
    // Intentionally does NOT depend on `selectedId` — reading selectedId here would cause the
    // effect to fire when the user clicks a new conversation, at which point Redux still holds
    // the previous conversation's messages, incorrectly caching them under the new ID.
    // Instead, fetchingForIdRef captures the correct target ID at dispatch time.
    useEffect(() => {
        const id = fetchingForIdRef.current;
        if (!id || fetchingMessages) return;
        // Fetch is complete — cache whatever arrived (even empty, to mark as "loaded")
        setMessageCache(prev => ({ ...prev, [id]: messages }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages, fetchingMessages]);

    // Always read from cache — never fall back to stale Redux messages from a previous conversation.
    // undefined cache entry means "not yet fetched"; [] means "fetched, no messages".
    const baseMessages = useMemo(() => {
        if (!selectedId) return [];
        return messageCache[selectedId] ?? [];
    }, [messageCache, selectedId]);

    // Merge real messages with optimistic ones (optimistic always appended at end)
    const displayMessages = useMemo(() => {
        const opts = (selectedId ? optimisticMsgs[selectedId] : null) || [];
        if (!opts.length) return baseMessages;
        const realTexts = new Set(baseMessages.map(m => m.text));
        const pending = opts.filter(o => !o.failed || !realTexts.has(o.text));
        return [
            ...baseMessages,
            ...pending.map(o => ({
                id: o.id,
                text: o.text,
                from_id: inboxStatus?.ig_user_id || '',
                from_name: null,
                created_at: o.created_at,
                _optimistic: true,
                _failed: o.failed,
            })),
        ];
    }, [baseMessages, optimisticMsgs, selectedId, inboxStatus]);

    // Show skeleton when the conversation has never been fetched (cache entry is undefined).
    // This fires immediately on conversation click — no waiting for fetchingMessages to flip.
    const isFirstLoad = Boolean(selectedId && messageCache[selectedId] === undefined);

    // Auto-scroll to bottom on first load and when optimistic messages are added
    useEffect(() => {
        if (!isFirstLoad) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [displayMessages, isFirstLoad]);

    const selected = useMemo(() => conversations.find(c => c.id === selectedId) || null, [conversations, selectedId]);

    // Derive peer name from message senders as last fallback (from_name in messages)
    const resolvedPeerName = useMemo(() => {
        if (!selected) return null;
        if (selected.peer_name) return selected.peer_name;
        // Look for from_name in messages where the sender is not the brand page
        const peerMsg = messages.find(m => m.from_id && m.from_id !== inboxStatus?.ig_user_id && m.from_name);
        return peerMsg?.from_name || null;
    }, [selected, messages, inboxStatus]);

    const recipientId = useMemo(() => {
        if (selected?.peer_id) return selected.peer_id;
        const parts = selected?.participants || [];
        return parts.find(p => p.id)?.id || selected?.last_message?.from_id || '';
    }, [selected]);

    const sendReply = () => {
        if (!brandId || !recipientId || !reply.trim() || !selectedId) {
            showToastMessage('Pick a thread and enter a reply', 'error');
            return;
        }
        const text = reply.trim();
        const tempId = `optimistic-${Date.now()}`;

        // 1. Show message instantly — clear input immediately
        setReply('');
        setOptimisticMsgs(prev => ({
            ...prev,
            [selectedId]: [...(prev[selectedId] || []), { id: tempId, text, created_at: new Date().toISOString() }],
        }));

        // 2. Fire API in background — no await blocking the UI
        dispatchP(sendBrandOpsInboxMessage({ brandId, recipient_id: recipientId, text }))
            .then(() => {
                // Remove optimistic message — real message will arrive on next fetch
                setOptimisticMsgs(prev => ({
                    ...prev,
                    [selectedId]: (prev[selectedId] || []).filter(m => m.id !== tempId),
                }));
                dispatch(fetchBrandOpsConversationMessages({ brandId, conversationId: selectedId }));
            })
            .catch((err: any) => {
                // Mark as failed so user sees the error inline
                setOptimisticMsgs(prev => ({
                    ...prev,
                    [selectedId]: (prev[selectedId] || []).map(m => (m.id === tempId ? { ...m, failed: true } : m)),
                }));
                showToastMessage(err?.message || 'Send failed', 'error');
            });
    };

    const suggestAi = async () => {
        if (!brandId) return;
        const seed = messages[messages.length - 1]?.text || selected?.last_message?.text || 'Customer inquiry';
        try {
            const result = (await dispatchP(
                runBrandOpsAiPlayground({
                    brandId,
                    message: seed,
                    format: 'instagram_dm',
                }),
            )) as IBrandOpsAiPlaygroundResult;
            const suggestion = getAiSuggestedText(result);
            if (!suggestion) {
                showToastMessage(HUMAN_ONLY_MESSAGE, 'error');
                return;
            }
            setReply(suggestion);
        } catch (err: any) {
            showToastMessage(err?.message || 'AI suggest failed', 'error');
        }
    };

    return (
        <BrandManageGate>
            <Box sx={MANAGE_PAGE_SX}>
                <ManagePageHeader
                    title="Inbox"
                    subtitle="Instagram DMs only (Meta) + comment→DM automation activity."
                    back={ROUTES.BRAND_MANAGE}
                    action={
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={tab}
                            onChange={(_, v) => v && setTab(v)}
                            sx={{
                                '& .MuiToggleButton-root': {
                                    color: BRAND_LUXE_MUTED,
                                    borderColor: 'rgba(255,255,255,0.12)',
                                    '&.Mui-selected': {
                                        color: BRAND_LUXE_PRIMARY,
                                        bgcolor: 'rgba(255,201,101,0.12)',
                                    },
                                },
                            }}>
                            <ToggleButton value="dms">DMs</ToggleButton>
                            <ToggleButton value="activity">Automation</ToggleButton>
                        </ToggleButtonGroup>
                    }
                />

                {inboxStatus !== null && !inboxStatus?.messaging_ready ? (
                    <Alert
                        severity="warning"
                        action={
                            <ManageGoldButton component={RouterLink as any} to={ROUTES.BRAND_MANAGE_SOCIAL}>
                                Connect Instagram
                            </ManageGoldButton>
                        }
                        sx={{
                            mb: 2,
                            bgcolor: 'rgba(255,201,101,0.08)',
                            color: '#fff',
                            border: '1px solid rgba(255,201,101,0.25)',
                            '& .MuiAlert-icon': { color: BRAND_LUXE_PRIMARY },
                        }}>
                        <strong>Connect Instagram with messaging permissions</strong> to read and reply to DMs. Only
                        Instagram DMs are supported.
                        {inboxStatus?.reason ? ` (${inboxStatus.reason})` : ''}
                    </Alert>
                ) : null}

                {tab === 'activity' ? (
                    <>
                        {/* Automation Rules */}
                        <ManageSurface sx={{ mb: 2, p: 0, overflow: 'hidden' }}>
                            {/* Card header */}
                            <Box
                                sx={{
                                    px: 2.5,
                                    pt: 2.5,
                                    pb: 2,
                                    background:
                                        'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(168,85,247,0.07) 100%)',
                                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box
                                        sx={{
                                            p: 0.75,
                                            borderRadius: 1.5,
                                            bgcolor: 'rgba(99,102,241,0.2)',
                                            display: 'flex',
                                        }}>
                                        <BoltOutlinedIcon sx={{ color: '#818cf8', fontSize: 18 }} />
                                    </Box>
                                    <Box>
                                        <Typography
                                            sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff', fontSize: 14 }}>
                                            Automation Rules
                                        </Typography>
                                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11, mt: 0.1 }}>
                                            Comment trigger → auto DM per post
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    {automations.length > 0 && (
                                        <Box
                                            sx={{
                                                px: 1.25,
                                                py: 0.4,
                                                borderRadius: 10,
                                                bgcolor: 'rgba(99,102,241,0.15)',
                                                border: '1px solid rgba(99,102,241,0.3)',
                                            }}>
                                            <Typography
                                                sx={{
                                                    color: '#818cf8',
                                                    fontSize: 11,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                }}>
                                                {automations.filter(r => r.automation.enabled).length}/
                                                {automations.length} active
                                            </Typography>
                                        </Box>
                                    )}
                                    <ManageGoldButton
                                        onClick={() => {
                                            setEditingRule(null);
                                            setAutomationModalOpen(true);
                                        }}>
                                        + New rule
                                    </ManageGoldButton>
                                </Box>
                            </Box>

                            {/* Rules list */}
                            <Box sx={{ px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {fetchingAutomations && !automations.length
                                    ? [0, 1].map(i => (
                                          <Box
                                              key={i}
                                              sx={{
                                                  p: 1.75,
                                                  borderRadius: 2,
                                                  bgcolor: 'rgba(255,255,255,0.03)',
                                                  border: '1px solid rgba(255,255,255,0.06)',
                                              }}>
                                              <Skeleton
                                                  variant="text"
                                                  width="40%"
                                                  height={16}
                                                  sx={{ bgcolor: 'rgba(255,255,255,0.07)', mb: 0.75 }}
                                              />
                                              <Skeleton
                                                  variant="text"
                                                  width="70%"
                                                  height={13}
                                                  sx={{ bgcolor: 'rgba(255,255,255,0.04)' }}
                                              />
                                          </Box>
                                      ))
                                    : null}

                                {!fetchingAutomations && !automations.length ? (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <BoltOutlinedIcon
                                            sx={{ color: 'rgba(255,255,255,0.1)', fontSize: 36, mb: 1 }}
                                        />
                                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 13 }}>
                                            No automation rules yet
                                        </Typography>
                                        <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontSize: 11, mt: 0.5 }}>
                                            Create a rule to auto-DM users who comment on your posts
                                        </Typography>
                                    </Box>
                                ) : null}

                                {automations.map(rule => {
                                    const enabled = rule.automation.enabled;
                                    return (
                                        <Box
                                            key={rule.automation._id}
                                            sx={{
                                                p: 1.75,
                                                borderRadius: 2,
                                                border: `1px solid ${
                                                    enabled ? 'rgba(99,102,241,0.25)' : 'rgba(255,255,255,0.07)'
                                                }`,
                                                bgcolor: enabled ? 'rgba(99,102,241,0.06)' : 'rgba(255,255,255,0.02)',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                gap: 2,
                                                flexWrap: 'wrap',
                                                transition: 'all 0.15s',
                                            }}>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 1,
                                                        alignItems: 'center',
                                                        flexWrap: 'wrap',
                                                        mb: 0.75,
                                                    }}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                            px: 1,
                                                            py: 0.3,
                                                            borderRadius: 1,
                                                            bgcolor: 'rgba(255,201,101,0.12)',
                                                            border: '1px solid rgba(255,201,101,0.25)',
                                                        }}>
                                                        <Typography sx={{ color: '#ffc965', fontSize: 11 }}>
                                                            💬
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                color: BRAND_LUXE_PRIMARY,
                                                                fontSize: 12,
                                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                            }}>
                                                            "{rule.automation.trigger_word}"
                                                        </Typography>
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.4,
                                                            px: 0.875,
                                                            py: 0.3,
                                                            borderRadius: 1,
                                                            bgcolor: enabled
                                                                ? 'rgba(52,211,153,0.1)'
                                                                : 'rgba(255,255,255,0.05)',
                                                            border: `1px solid ${
                                                                enabled
                                                                    ? 'rgba(52,211,153,0.3)'
                                                                    : 'rgba(255,255,255,0.1)'
                                                            }`,
                                                        }}>
                                                        <Box
                                                            sx={{
                                                                width: 5,
                                                                height: 5,
                                                                borderRadius: '50%',
                                                                bgcolor: enabled ? '#34d399' : 'rgba(255,255,255,0.3)',
                                                            }}
                                                        />
                                                        <Typography
                                                            sx={{
                                                                color: enabled ? '#34d399' : BRAND_LUXE_MUTED,
                                                                fontSize: 11,
                                                            }}>
                                                            {enabled ? 'Active' : 'Paused'}
                                                        </Typography>
                                                    </Box>
                                                    {(rule.automation.stats?.comments_handled ?? 0) > 0 && (
                                                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11 }}>
                                                            {rule.automation.stats!.comments_handled} handled
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                                                    <ChatBubbleOutlineIcon
                                                        sx={{
                                                            color: 'rgba(255,255,255,0.2)',
                                                            fontSize: 13,
                                                            mt: 0.15,
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <Typography
                                                        sx={{
                                                            color: BRAND_LUXE_MUTED,
                                                            fontSize: 12,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                        {rule.automation.dm_message}
                                                    </Typography>
                                                </Box>
                                                {rule.caption && (
                                                    <Typography
                                                        sx={{ color: 'rgba(255,255,255,0.2)', fontSize: 11, mt: 0.4 }}>
                                                        Post: {rule.caption.slice(0, 60)}
                                                        {rule.caption.length > 60 ? '…' : ''}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
                                                <Switch
                                                    size="small"
                                                    checked={enabled}
                                                    disabled={mutatingAutomation}
                                                    onChange={e => {
                                                        if (!brandId) return;
                                                        dispatchP(
                                                            toggleBrandOpsAutomation({
                                                                brandId,
                                                                automationId: rule.automation._id,
                                                                enabled: e.target.checked,
                                                            }),
                                                        ).catch((err: any) =>
                                                            showToastMessage(err?.message || 'Failed', 'error'),
                                                        );
                                                    }}
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#34d399' },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                            bgcolor: '#34d399',
                                                        },
                                                    }}
                                                />
                                                <Box
                                                    onClick={() => {
                                                        setEditingRule(rule);
                                                        setAutomationModalOpen(true);
                                                    }}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        px: 1.25,
                                                        py: 0.5,
                                                        borderRadius: 1.5,
                                                        border: '1px solid rgba(255,255,255,0.1)',
                                                        color: BRAND_LUXE_MUTED,
                                                        fontSize: 12,
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            borderColor: 'rgba(255,255,255,0.25)',
                                                            color: '#fff',
                                                        },
                                                        transition: 'all 0.15s',
                                                    }}>
                                                    <EditOutlinedIcon sx={{ fontSize: 13 }} />
                                                    Edit
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </ManageSurface>

                        {/* Activity Log */}
                        <ManageSurface sx={{ p: 0, overflow: 'hidden' }}>
                            <Box
                                sx={{
                                    px: 2.5,
                                    pt: 2,
                                    pb: 1.75,
                                    background:
                                        'linear-gradient(135deg, rgba(20,184,166,0.08) 0%, rgba(6,182,212,0.05) 100%)',
                                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}>
                                <Box
                                    sx={{
                                        p: 0.75,
                                        borderRadius: 1.5,
                                        bgcolor: 'rgba(20,184,166,0.15)',
                                        display: 'flex',
                                    }}>
                                    <HistoryOutlinedIcon sx={{ color: '#2dd4bf', fontSize: 18 }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff', fontSize: 14 }}>
                                        Activity Log
                                    </Typography>
                                    <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11 }}>
                                        Recent automation events
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 0 }}>
                                {fetchingActivity && !activity.length
                                    ? [0, 1, 2].map(i => (
                                          <Box
                                              key={i}
                                              sx={{
                                                  display: 'flex',
                                                  gap: 1.5,
                                                  py: 1.25,
                                                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                                              }}>
                                              <Skeleton
                                                  variant="circular"
                                                  width={28}
                                                  height={28}
                                                  sx={{ bgcolor: 'rgba(255,255,255,0.07)', flexShrink: 0 }}
                                              />
                                              <Box sx={{ flex: 1 }}>
                                                  <Skeleton
                                                      variant="text"
                                                      width="50%"
                                                      height={14}
                                                      sx={{ bgcolor: 'rgba(255,255,255,0.06)' }}
                                                  />
                                                  <Skeleton
                                                      variant="text"
                                                      width="70%"
                                                      height={12}
                                                      sx={{ bgcolor: 'rgba(255,255,255,0.04)', mt: 0.5 }}
                                                  />
                                              </Box>
                                          </Box>
                                      ))
                                    : null}

                                {!fetchingActivity && !activity.length ? (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <HistoryOutlinedIcon
                                            sx={{ color: 'rgba(255,255,255,0.1)', fontSize: 36, mb: 1 }}
                                        />
                                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 13 }}>
                                            No automation activity yet
                                        </Typography>
                                    </Box>
                                ) : null}

                                {activity.map((item, idx) => {
                                    const sent = item.private_reply_sent;
                                    const hasErr = Boolean(item.error_message);
                                    return (
                                        <Box
                                            key={item._id}
                                            sx={{
                                                display: 'flex',
                                                gap: 1.5,
                                                py: 1.5,
                                                borderBottom:
                                                    idx < activity.length - 1
                                                        ? '1px solid rgba(255,255,255,0.04)'
                                                        : 'none',
                                                alignItems: 'flex-start',
                                            }}>
                                            {/* Timeline icon */}
                                            <Box
                                                sx={{
                                                    width: 28,
                                                    height: 28,
                                                    borderRadius: '50%',
                                                    flexShrink: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    bgcolor: hasErr
                                                        ? 'rgba(239,68,68,0.12)'
                                                        : sent
                                                        ? 'rgba(52,211,153,0.12)'
                                                        : 'rgba(255,255,255,0.06)',
                                                    border: `1px solid ${
                                                        hasErr
                                                            ? 'rgba(239,68,68,0.3)'
                                                            : sent
                                                            ? 'rgba(52,211,153,0.3)'
                                                            : 'rgba(255,255,255,0.1)'
                                                    }`,
                                                }}>
                                                {hasErr ? (
                                                    <ErrorOutlineIcon sx={{ fontSize: 14, color: '#f87171' }} />
                                                ) : sent ? (
                                                    <CheckCircleOutlineIcon sx={{ fontSize: 14, color: '#34d399' }} />
                                                ) : (
                                                    <ChatBubbleOutlineIcon
                                                        sx={{ fontSize: 14, color: BRAND_LUXE_MUTED }}
                                                    />
                                                )}
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        flexWrap: 'wrap',
                                                    }}>
                                                    <Typography
                                                        sx={{
                                                            color: '#fff',
                                                            fontSize: 13,
                                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        }}>
                                                        {sent
                                                            ? 'DM sent'
                                                            : hasErr
                                                            ? 'Automation error'
                                                            : 'Comment detected'}
                                                    </Typography>
                                                    {item.trigger_word && (
                                                        <Box
                                                            sx={{
                                                                px: 0.75,
                                                                py: 0.2,
                                                                borderRadius: 1,
                                                                bgcolor: 'rgba(255,201,101,0.1)',
                                                                border: '1px solid rgba(255,201,101,0.2)',
                                                            }}>
                                                            <Typography
                                                                sx={{ color: BRAND_LUXE_PRIMARY, fontSize: 10 }}>
                                                                "{item.trigger_word}"
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.35 }}>
                                                    {item.ig_comment_id && (
                                                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11 }}>
                                                            comment {item.ig_comment_id.slice(-8)}
                                                        </Typography>
                                                    )}
                                                    {item.createdAt && (
                                                        <Typography
                                                            sx={{ color: 'rgba(255,255,255,0.2)', fontSize: 11 }}>
                                                            · {relativeTime(item.createdAt)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                {hasErr && (
                                                    <Typography sx={{ color: '#f87171', fontSize: 11, mt: 0.35 }}>
                                                        {item.error_message}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </ManageSurface>

                        <BrandManageAutomationRuleModal
                            brandId={brandId || ''}
                            open={automationModalOpen}
                            onClose={() => setAutomationModalOpen(false)}
                            existing={editingRule}
                            igPosts={igPublishedPosts}
                        />
                    </>
                ) : (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
                            gap: 2,
                            minHeight: 520,
                            alignItems: 'start',
                        }}>
                        {/* Conversation list */}
                        <ManageSurface sx={{ p: 0, overflow: 'hidden' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    px: 2,
                                    pt: 2,
                                    pb: 1.25,
                                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                                }}>
                                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff', fontSize: 14 }}>
                                    Messages
                                </Typography>
                                <ManageGoldButton
                                    sx={{ bgcolor: 'transparent', color: BRAND_LUXE_MUTED, fontSize: 12, px: 1 }}
                                    onClick={() =>
                                        brandId && dispatch(fetchBrandOpsConversations({ brandId, force: true }))
                                    }>
                                    Refresh
                                </ManageGoldButton>
                            </Box>
                            <Box sx={{ maxHeight: 520, overflow: 'auto' }}>
                                {fetchingConversations && !conversations.length ? (
                                    <Box sx={{ px: 2, pt: 1 }}>
                                        {Array.from({ length: 6 }).map((_, i) => (
                                            <Box
                                                key={i}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1.5,
                                                    py: 1.5,
                                                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                                                }}>
                                                <Skeleton
                                                    variant="circular"
                                                    width={44}
                                                    height={44}
                                                    sx={{ bgcolor: 'rgba(255,255,255,0.08)', flexShrink: 0 }}
                                                />
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Skeleton
                                                        variant="text"
                                                        width="55%"
                                                        height={16}
                                                        sx={{ bgcolor: 'rgba(255,255,255,0.08)', mb: 0.75 }}
                                                    />
                                                    <Skeleton
                                                        variant="text"
                                                        width="80%"
                                                        height={13}
                                                        sx={{ bgcolor: 'rgba(255,255,255,0.05)' }}
                                                    />
                                                </Box>
                                                <Skeleton
                                                    variant="text"
                                                    width={28}
                                                    height={12}
                                                    sx={{ bgcolor: 'rgba(255,255,255,0.05)', flexShrink: 0 }}
                                                />
                                            </Box>
                                        ))}
                                    </Box>
                                ) : null}
                                {!fetchingConversations && !conversations.length ? (
                                    <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 13, px: 2, py: 2 }}>
                                        No conversations yet.
                                    </Typography>
                                ) : null}
                                {conversations.map(c => {
                                    const peerId = c.peer_id || c.participants?.find(p => p.id)?.id || '';
                                    const displayName = formatPeerName(c.peer_name, peerId);
                                    const gradient = avatarGradient(peerId);
                                    const initials = c.peer_name
                                        ? c.peer_name
                                              .split(' ')
                                              .map(w => w[0])
                                              .join('')
                                              .slice(0, 2)
                                              .toUpperCase()
                                        : peerId
                                        ? peerId.slice(-2).toUpperCase()
                                        : 'IG';
                                    const lastMsg = c.last_message;
                                    const preview = lastMsg?.text?.trim() || (lastMsg ? '📷 Media message' : '');
                                    const ts = relativeTime(lastMsg?.created_at || c.updated_at);
                                    const isSelected = selectedId === c.id;
                                    return (
                                        <Box
                                            key={c.id}
                                            onClick={() => setSelectedId(c.id)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                px: 2,
                                                py: 1.5,
                                                cursor: 'pointer',
                                                borderBottom: '1px solid rgba(255,255,255,0.05)',
                                                bgcolor: isSelected ? 'rgba(255,201,101,0.10)' : 'transparent',
                                                borderLeft: isSelected
                                                    ? `3px solid ${BRAND_LUXE_PRIMARY}`
                                                    : '3px solid transparent',
                                                transition: 'background 0.15s',
                                                '&:hover': {
                                                    bgcolor: isSelected
                                                        ? 'rgba(255,201,101,0.10)'
                                                        : 'rgba(255,255,255,0.04)',
                                                },
                                            }}>
                                            <Avatar
                                                src={c.peer_profile_pic || undefined}
                                                sx={{
                                                    width: 44,
                                                    height: 44,
                                                    background: gradient,
                                                    fontSize: 13,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    flexShrink: 0,
                                                    border: isSelected
                                                        ? `2px solid ${BRAND_LUXE_PRIMARY}`
                                                        : '2px solid transparent',
                                                    boxShadow: isSelected ? `0 0 0 2px rgba(255,201,101,0.25)` : 'none',
                                                    transition: 'border 0.15s, box-shadow 0.15s',
                                                }}>
                                                {initials}
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'baseline',
                                                        gap: 1,
                                                    }}>
                                                    <Typography
                                                        sx={{
                                                            color: '#fff',
                                                            fontSize: 13,
                                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                        {displayName}
                                                    </Typography>
                                                    {ts ? (
                                                        <Typography
                                                            sx={{
                                                                color: BRAND_LUXE_MUTED,
                                                                fontSize: 11,
                                                                whiteSpace: 'nowrap',
                                                                flexShrink: 0,
                                                            }}>
                                                            {ts}
                                                        </Typography>
                                                    ) : null}
                                                </Box>
                                                <Box
                                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                                                    {c.unread_hint ? (
                                                        <Box
                                                            sx={{
                                                                width: 7,
                                                                height: 7,
                                                                borderRadius: '50%',
                                                                bgcolor: BRAND_LUXE_PRIMARY,
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                    ) : null}
                                                    <Typography
                                                        sx={{
                                                            color: c.unread_hint
                                                                ? 'rgba(255,255,255,0.75)'
                                                                : BRAND_LUXE_MUTED,
                                                            fontSize: 12,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            fontFamily: c.unread_hint ? FONT_ENUM.MAIN_BOLD : undefined,
                                                        }}>
                                                        {preview || 'No messages yet'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </ManageSurface>

                        {/* Thread pane */}
                        <ManageSurface
                            sx={{ display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden', minHeight: 520 }}>
                            {!selectedId ? (
                                <Box
                                    sx={{
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: 520,
                                    }}>
                                    <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 13 }}>
                                        Select a conversation to view messages
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    {/* Thread header */}
                                    {selected ? (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                px: 2,
                                                py: 1.5,
                                                borderBottom: '1px solid rgba(255,255,255,0.06)',
                                                background: 'rgba(255,255,255,0.02)',
                                            }}>
                                            <Box sx={{ position: 'relative' }}>
                                                <Avatar
                                                    src={selected.peer_profile_pic || undefined}
                                                    sx={{
                                                        width: 38,
                                                        height: 38,
                                                        background: avatarGradient(selected.peer_id),
                                                        fontSize: 12,
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    }}>
                                                    {resolvedPeerName
                                                        ? resolvedPeerName
                                                              .split(' ')
                                                              .map((w: string) => w[0])
                                                              .join('')
                                                              .slice(0, 2)
                                                              .toUpperCase()
                                                        : (selected.peer_id || '').slice(-2).toUpperCase() || 'IG'}
                                                </Avatar>
                                            </Box>
                                            <Box>
                                                <Typography
                                                    sx={{
                                                        color: '#fff',
                                                        fontSize: 14,
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    }}>
                                                    {formatPeerName(resolvedPeerName, selected.peer_id)}
                                                </Typography>
                                                <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11 }}>
                                                    Instagram · DM
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ) : null}

                                    {/* Messages */}
                                    <Box
                                        sx={{
                                            flex: 1,
                                            overflow: 'auto',
                                            px: 2,
                                            py: 1.5,
                                            maxHeight: 360,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                        }}>
                                        {isFirstLoad ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                {[
                                                    ['40%', false],
                                                    ['60%', true],
                                                    ['35%', false],
                                                    ['55%', true],
                                                    ['45%', false],
                                                    ['50%', true],
                                                ].map(([w, sent], i) => (
                                                    <Box
                                                        key={i}
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: sent ? 'flex-end' : 'flex-start',
                                                        }}>
                                                        <Skeleton
                                                            variant="rounded"
                                                            width={w as string}
                                                            height={36}
                                                            sx={{
                                                                bgcolor: sent
                                                                    ? 'rgba(255,201,101,0.10)'
                                                                    : 'rgba(255,255,255,0.07)',
                                                                borderRadius: sent
                                                                    ? '18px 18px 4px 18px'
                                                                    : '18px 18px 18px 4px',
                                                            }}
                                                        />
                                                        <Skeleton
                                                            variant="text"
                                                            width={32}
                                                            height={11}
                                                            sx={{ bgcolor: 'rgba(255,255,255,0.04)', mt: 0.5, mx: 0.5 }}
                                                        />
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : null}
                                        {!isFirstLoad && !displayMessages.length ? (
                                            <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 13 }}>
                                                No messages in this thread.
                                            </Typography>
                                        ) : null}
                                        {displayMessages.map((m: any) => {
                                            const isSent = m.from_id === inboxStatus?.ig_user_id;
                                            const hasText = Boolean(m.text?.trim());
                                            const attachments = m.attachments || [];
                                            const hasMedia = m.has_media === true;
                                            const urlsInText: string[] = hasText
                                                ? Array.from(
                                                      m.text.matchAll(/https?:\/\/[^\s,，!?。]+/gi),
                                                      (match: RegExpMatchArray) => match[0],
                                                  )
                                                : [];
                                            return (
                                                <Box
                                                    key={m.id}
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: isSent ? 'flex-end' : 'flex-start',
                                                        gap: 0.5,
                                                    }}>
                                                    {/* Attachment media */}
                                                    {attachments.map((att: any, ai: number) => (
                                                        <Box
                                                            key={ai}
                                                            sx={{
                                                                maxWidth: '68%',
                                                                borderRadius: isSent
                                                                    ? '14px 14px 4px 14px'
                                                                    : '14px 14px 14px 4px',
                                                                overflow: 'hidden',
                                                                border: isSent
                                                                    ? '1px solid rgba(255,201,101,0.25)'
                                                                    : '1px solid rgba(255,255,255,0.10)',
                                                            }}>
                                                            {att.type === 'image' ? (
                                                                <Box
                                                                    component="img"
                                                                    src={att.url}
                                                                    alt="image"
                                                                    sx={{
                                                                        display: 'block',
                                                                        maxWidth: '100%',
                                                                        maxHeight: 260,
                                                                        objectFit: 'cover',
                                                                        cursor: 'pointer',
                                                                    }}
                                                                    onClick={() => window.open(att.url, '_blank')}
                                                                />
                                                            ) : att.type === 'video' ? (
                                                                <Box
                                                                    component="video"
                                                                    src={att.url}
                                                                    controls
                                                                    sx={{
                                                                        display: 'block',
                                                                        maxWidth: '100%',
                                                                        maxHeight: 260,
                                                                    }}
                                                                />
                                                            ) : (
                                                                <Box
                                                                    component="a"
                                                                    href={att.url}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    sx={{
                                                                        display: 'block',
                                                                        px: 1.5,
                                                                        py: 1,
                                                                        color: BRAND_LUXE_PRIMARY,
                                                                        fontSize: 13,
                                                                        textDecoration: 'none',
                                                                        bgcolor: 'rgba(255,255,255,0.06)',
                                                                        '&:hover': { textDecoration: 'underline' },
                                                                    }}>
                                                                    📎 Attachment
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    ))}
                                                    {/* Text bubble — only render if there's text */}
                                                    {hasText ? (
                                                        <Box
                                                            sx={{
                                                                maxWidth: '72%',
                                                                px: 1.75,
                                                                py: 1,
                                                                borderRadius: isSent
                                                                    ? '18px 18px 4px 18px'
                                                                    : '18px 18px 18px 4px',
                                                                background: isSent
                                                                    ? 'linear-gradient(135deg, rgba(255,201,101,0.28) 0%, rgba(255,160,60,0.22) 100%)'
                                                                    : 'rgba(255,255,255,0.08)',
                                                                border: isSent
                                                                    ? '1px solid rgba(255,201,101,0.35)'
                                                                    : '1px solid rgba(255,255,255,0.10)',
                                                                backdropFilter: 'blur(4px)',
                                                            }}>
                                                            <Typography
                                                                sx={{
                                                                    color: isSent
                                                                        ? '#ffe566'
                                                                        : 'rgba(255,255,255,0.92)',
                                                                    fontSize: 13.5,
                                                                    whiteSpace: 'pre-wrap',
                                                                    lineHeight: 1.55,
                                                                }}>
                                                                {m.text}
                                                            </Typography>
                                                            {/* Link CTA cards */}
                                                            {urlsInText.length > 0 && (
                                                                <Box
                                                                    sx={{
                                                                        mt: 1,
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        gap: 0.75,
                                                                    }}>
                                                                    {urlsInText.map((url, ui) => {
                                                                        let displayUrl = url;
                                                                        try {
                                                                            displayUrl = new URL(url).hostname.replace(
                                                                                /^www\./,
                                                                                '',
                                                                            );
                                                                        } catch {}
                                                                        return (
                                                                            <Box
                                                                                key={ui}
                                                                                component="a"
                                                                                href={url}
                                                                                target="_blank"
                                                                                rel="noreferrer noopener"
                                                                                sx={{
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    gap: 0.75,
                                                                                    px: 1.5,
                                                                                    py: 0.9,
                                                                                    mt: 0.25,
                                                                                    borderRadius: 2,
                                                                                    bgcolor: isSent
                                                                                        ? 'rgba(255,201,101,0.18)'
                                                                                        : 'rgba(255,255,255,0.1)',
                                                                                    border: `1px solid ${
                                                                                        isSent
                                                                                            ? 'rgba(255,201,101,0.35)'
                                                                                            : 'rgba(255,255,255,0.18)'
                                                                                    }`,
                                                                                    textDecoration: 'none',
                                                                                    transition: 'background 0.15s',
                                                                                    '&:hover': {
                                                                                        bgcolor: isSent
                                                                                            ? 'rgba(255,201,101,0.28)'
                                                                                            : 'rgba(255,255,255,0.16)',
                                                                                    },
                                                                                }}>
                                                                                <Typography
                                                                                    sx={{
                                                                                        fontSize: 12.5,
                                                                                        color: isSent
                                                                                            ? '#ffc965'
                                                                                            : 'rgba(255,255,255,0.85)',
                                                                                        fontFamily: 'inherit',
                                                                                        fontWeight: 600,
                                                                                    }}>
                                                                                    {displayUrl}
                                                                                </Typography>
                                                                                <Typography
                                                                                    sx={{
                                                                                        fontSize: 11,
                                                                                        color: isSent
                                                                                            ? 'rgba(255,201,101,0.6)'
                                                                                            : 'rgba(255,255,255,0.35)',
                                                                                    }}>
                                                                                    ↗
                                                                                </Typography>
                                                                            </Box>
                                                                        );
                                                                    })}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    ) : hasMedia ? (
                                                        /* Placeholder for media messages whose URLs Instagram withholds */
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 0.75,
                                                                px: 1.5,
                                                                py: 0.875,
                                                                borderRadius: isSent
                                                                    ? '14px 14px 4px 14px'
                                                                    : '14px 14px 14px 4px',
                                                                background: 'rgba(255,255,255,0.04)',
                                                                border: '1px dashed rgba(255,255,255,0.15)',
                                                            }}>
                                                            <Typography sx={{ fontSize: 15, lineHeight: 1 }}>
                                                                🖼️
                                                            </Typography>
                                                            <Typography
                                                                sx={{
                                                                    color: 'rgba(255,255,255,0.4)',
                                                                    fontSize: 12,
                                                                    fontStyle: 'italic',
                                                                }}>
                                                                Media message
                                                            </Typography>
                                                        </Box>
                                                    ) : null}
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                            mt: 0.25,
                                                            mx: 0.75,
                                                        }}>
                                                        {m.created_at ? (
                                                            <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 10 }}>
                                                                {relativeTime(m.created_at)}
                                                            </Typography>
                                                        ) : null}
                                                        {m._optimistic && !m._failed ? (
                                                            <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 10 }}>
                                                                · Sending…
                                                            </Typography>
                                                        ) : null}
                                                        {m._failed ? (
                                                            <Typography sx={{ color: '#ff6b6b', fontSize: 10 }}>
                                                                · Failed to send
                                                            </Typography>
                                                        ) : null}
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                        <div ref={messagesEndRef} />
                                    </Box>

                                    {/* Reply box */}
                                    <Box
                                        sx={{
                                            px: 2,
                                            pb: 2,
                                            pt: 1,
                                            borderTop: '1px solid rgba(255,255,255,0.06)',
                                        }}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={2}
                                            placeholder="Write a reply…"
                                            value={reply}
                                            onChange={e => setReply(e.target.value)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) sendReply();
                                            }}
                                            sx={{
                                                mb: 1,
                                                '& .MuiOutlinedInput-root': {
                                                    color: '#fff',
                                                    bgcolor: 'rgba(255,255,255,0.04)',
                                                    borderRadius: 2,
                                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                                                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                                    '&.Mui-focused fieldset': { borderColor: BRAND_LUXE_PRIMARY },
                                                },
                                                '& .MuiInputBase-input::placeholder': { color: BRAND_LUXE_MUTED },
                                            }}
                                        />
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                            <ManageGoldButton onClick={sendReply} disabled={!reply.trim()}>
                                                Send
                                            </ManageGoldButton>
                                            <ManageGoldButton
                                                onClick={suggestAi}
                                                disabled={aiBusy}
                                                sx={{
                                                    bgcolor: 'transparent',
                                                    color: BRAND_LUXE_PRIMARY,
                                                    border: '1px solid rgba(255,201,101,0.35)',
                                                }}>
                                                {aiBusy ? 'AI…' : 'AI suggest'}
                                            </ManageGoldButton>
                                            <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11, ml: 'auto' }}>
                                                ⌘↵ to send
                                            </Typography>
                                        </Box>
                                    </Box>
                                </>
                            )}
                        </ManageSurface>
                    </Box>
                )}
            </Box>
        </BrandManageGate>
    );
}
